import { Grid, CardHeader, Stack, Container } from "@mui/material";
import { useMemo } from "react";
import { useOutletContext } from "react-router";
import { Typography } from "@platform/design-system";

import { useBrokerBonus } from "../../hooks/useBbe";
import { CircularProgressCentered } from "../../components/CircularProgressCentered";

import { ColorsMap, StackedBarCard } from "./components/stackedBarChart";
import { CarrierBonusSummaryTable } from "./components/carrierBonusSummaryTable";
import { BBEOutletContext, ChartData } from "./types";
import { mapForecastToBarChartSeries } from "./utils";

export const Forecast = (): React.JSX.Element => {
  const { brokerId } = useOutletContext<BBEOutletContext>();

  const forecastChartsQuery = useBrokerBonus(brokerId);

  const totalBonusSeries: {
    series: ChartData;
    colorsMap?: ColorsMap;
  } = useMemo(
    () =>
      mapForecastToBarChartSeries(
        "total_bonus",
        "bonus",
        forecastChartsQuery.data,
      ),
    [forecastChartsQuery.data],
  );

  const bobByCarrierSeries: {
    series: ChartData;
    colorsMap?: ColorsMap;
  } = useMemo(
    () => mapForecastToBarChartSeries("bob", "bob", forecastChartsQuery.data),
    [forecastChartsQuery.data],
  );

  const premiumByLocSeries: {
    series: ChartData;
    colorsMap?: ColorsMap;
  } = useMemo(
    () =>
      mapForecastToBarChartSeries(
        "premium_by_loc",
        "Premium",
        forecastChartsQuery.data,
      ),
    [forecastChartsQuery.data],
  );

  if (!brokerId) {
    return (
      <Typography color="danger" sx={{ mt: 4 }}>
        Select a broker to view forecast data.
      </Typography>
    );
  }

  if (
    !forecastChartsQuery.data &&
    (forecastChartsQuery.isLoading || forecastChartsQuery.isFetching)
  ) {
    return <CircularProgressCentered />;
  }

  return (
    <Container>
      {/* Subtitle with sync timestamp */}
      <CardHeader
        title={
          <Typography variant="header-medium" sx={{ fontWeight: 400 }}>
            Bonus Forecast
          </Typography>
        }
        // TODO: Update once we are able to retrieve
        // subheader={
        //   <Typography variant="body-medium" color="textSecondary">
        //     Last Salesforce sync {}
        //   </Typography>
        // }
        sx={{ px: 0, textAlign: "left" }}
      />

      <Stack spacing={3}>
        <Grid container spacing={3}>
          {/* Three stacked bar charts */}
          <Grid size={{ xs: 12, md: 4 }} sx={{ display: "flex" }}>
            <StackedBarCard
              title="Total Bonus by Carrier"
              series={totalBonusSeries.series}
              colorsMap={totalBonusSeries.colorsMap}
              showSegmentPercents={true}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }} sx={{ display: "flex" }}>
            <StackedBarCard
              title="BOB by Carrier"
              series={bobByCarrierSeries.series}
              colorsMap={bobByCarrierSeries.colorsMap}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }} sx={{ display: "flex" }}>
            <StackedBarCard
              title="Premium by LOC"
              series={premiumByLocSeries.series}
              colorsMap={premiumByLocSeries.colorsMap}
            />
          </Grid>
        </Grid>

        {/* Carrier Bonus Summary Table */}
        <CarrierBonusSummaryTable brokerId={brokerId} />
      </Stack>
    </Container>
  );
};

export default Forecast;
